import React, { useState } from "react";
import { FiArrowLeft, FiCreditCard, FiLock, FiTruck } from "react-icons/fi";
import { Link } from "react-router-dom";
import Button from "../components/common/Button";
import EmptyState from "../components/common/EmptyState";
import PageMeta from "../components/common/PageMeta";
import siteConfig, { formatCurrency } from "../config/siteConfig";
import { useCommerce } from "../hooks/useCommerce";
import { buildPaymentUrl, createOrderNumber } from "../services/checkout";

const supportedCountries = [
  "Australia", "Austria", "Belgium", "Brazil", "Canada", "Chile", "Croatia",
  "Cyprus", "Czech Republic", "Denmark", "Estonia", "Finland", "France",
  "Germany", "Greece", "Hong Kong", "Hungary", "Iceland", "Ireland", "Israel",
  "Italy", "Japan", "Latvia", "Lithuania", "Luxembourg", "Malaysia", "Malta",
  "Mexico", "Netherlands", "New Zealand", "Norway", "Poland", "Portugal",
  "Romania", "Singapore", "Slovakia", "Slovenia", "South Korea", "Spain",
  "Sweden", "Switzerland", "Thailand", "United Arab Emirates", "United Kingdom",
  "United States", "Vietnam",
];

const initialForm = {
  email: "", firstName: "", lastName: "", address: "", apartment: "", city: "", state: "", postalCode: "", country: "Hong Kong", notes: "",
};

const CheckoutField = ({ name, label, type = "text", half = false, autoComplete, values, errors, onChange }) => (
  <label className={`checkout-field ${half ? "checkout-field--half" : ""}`}>
    <span>{label}</span>
    <input
      name={name}
      type={type}
      value={values[name]}
      onChange={onChange}
      autoComplete={autoComplete}
      aria-invalid={Boolean(errors[name])}
      aria-describedby={errors[name] ? `${name}-error` : undefined}
    />
    {errors[name] && <small className="field-error" id={`${name}-error`}>{errors[name]}</small>}
  </label>
);

const CheckoutPage = () => {
  const commerce = useCommerce();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const deliveryMethod = "standard";
  const [status, setStatus] = useState("idle");
  const [submitError, setSubmitError] = useState("");

  const shippingCost = siteConfig.shipping.standardRate;
  const orderTotal = commerce.subtotal + shippingCost;

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    ["email", "firstName", "lastName", "address", "city", "state", "postalCode"].forEach((field) => {
      if (!form[field].trim()) next[field] = "This field is required.";
    });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email address.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const placeOrder = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    setSubmitError("");
    try {
      const orderNo = createOrderNumber();
      const order = {
        id: orderNo,
        createdAt: new Date().toISOString(),
        customer: form,
        deliveryMethod,
        items: commerce.cartItems,
        totals: { subtotal: commerce.subtotal, shipping: shippingCost, tax: 0, total: orderTotal },
        status: "pending_payment",
      };
      window.sessionStorage.setItem("madezuno:last-order", JSON.stringify(order));
      window.location.assign(buildPaymentUrl({
        orderNo,
        amount: orderTotal,
        currency: siteConfig.currency.code,
        email: form.email,
        title: commerce.cartItems[0].product.name,
      }));
    } catch (error) {
      setSubmitError(error.message || "We could not place the order. Please try again.");
      setStatus("error");
    }
  };

  if (!commerce.cartItems.length) {
    return <div className="checkout-page"><PageMeta title="Checkout" /><EmptyState title="There is nothing to check out yet" description="Add a few pantry essentials before continuing." actionLabel="Shop spices" actionTo="/shop" /></div>;
  }

  const fieldProps = { values: form, errors, onChange: update };

  return (
    <div className="checkout-page">
      <PageMeta title="Checkout" description={`Complete your ${siteConfig.displayName} order.`} />
      <div className="site-container checkout-page__header"><Link to="/cart"><FiArrowLeft /> Return to cart</Link><span><FiLock /> Secure checkout</span></div>
      <form className="site-container checkout-layout" onSubmit={placeOrder} noValidate>
        <section className="checkout-form" aria-label="Checkout details">
          <section className="checkout-section">
            <div className="checkout-section__heading"><span>1</span><div><p className="eyebrow">Contact</p><h1>Where should we send updates?</h1></div></div>
            <CheckoutField {...fieldProps} name="email" label="Email address" type="email" autoComplete="email" />
          </section>
          <section className="checkout-section">
            <div className="checkout-section__heading"><span>2</span><div><p className="eyebrow">Delivery</p><h2>Shipping address</h2></div></div>
            <div className="checkout-form__row"><CheckoutField {...fieldProps} name="firstName" label="First name" half autoComplete="given-name" /><CheckoutField {...fieldProps} name="lastName" label="Last name" half autoComplete="family-name" /></div>
            <CheckoutField {...fieldProps} name="address" label="Street address" autoComplete="street-address" />
            <CheckoutField {...fieldProps} name="apartment" label="Apartment, suite, etc. (optional)" />
            <label className="checkout-field"><span>Country / region</span><select name="country" value={form.country} onChange={update} autoComplete="country-name">{supportedCountries.map((country) => <option key={country} value={country}>{country}</option>)}</select></label>
            <div className="checkout-form__row checkout-form__row--three"><CheckoutField {...fieldProps} name="city" label="City" /><CheckoutField {...fieldProps} name="state" label="State" /><CheckoutField {...fieldProps} name="postalCode" label="ZIP code" autoComplete="postal-code" /></div>
            <div className="delivery-options">
              <label className="is-selected"><input type="radio" name="delivery" value="standard" checked readOnly /><FiTruck /><span><strong>Standard shipping</strong><small>{siteConfig.shipping.estimatedDelivery}</small></span><strong>{formatCurrency(siteConfig.shipping.standardRate)}</strong></label>
            </div>
          </section>
          <section className="checkout-section">
            <div className="checkout-section__heading"><span>3</span><div><p className="eyebrow">Payment</p><h2>Continue to secure payment</h2></div></div>
            <div className="payment-options">
              <label className="is-selected"><input type="radio" name="payment" checked readOnly /><FiCreditCard /><span><strong>Secure card payment</strong><small>Card details are entered on the secure payment page.</small></span></label>
            </div>
            <div className="secure-payment-placeholder"><FiLock aria-hidden="true" /><div><strong>Your payment details stay with the payment provider.</strong><p>madezuno does not collect card numbers on this form.</p></div></div>
            <label className="checkout-field"><span>Delivery note (optional)</span><textarea name="notes" value={form.notes} onChange={update} rows="3" /></label>
          </section>
          {submitError && <div className="form-submit-error" role="alert">{submitError}</div>}
          <Button type="submit" size="large" fullWidth loading={status === "submitting"} loadingLabel="Redirecting">Pay now · {formatCurrency(orderTotal)}</Button>
          <p className="checkout-form__terms">By continuing, you agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
        </section>
        <aside className="checkout-summary">
          <p className="eyebrow">Order summary</p><h2>{commerce.cartCount} items</h2>
          <ul>{commerce.cartItems.map((item) => <li key={item.lineId}><span className="checkout-summary__image"><img src={item.image} alt="" /><small>{item.quantity}</small></span><span><strong>{item.product.name}</strong><small>{item.variantLabel} · {item.variantWeight}</small></span><strong>{formatCurrency(item.lineTotal)}</strong></li>)}</ul>
          <dl><div><dt>Complimentary sample</dt><dd>{commerce.freeSample === "surprise" ? "Surprise me" : commerce.freeSample}</dd></div><div><dt>Subtotal</dt><dd>{formatCurrency(commerce.subtotal)}</dd></div><div><dt>Shipping</dt><dd>{formatCurrency(shippingCost)}</dd></div><div><dt>Estimated tax</dt><dd>Calculated later</dd></div><div className="checkout-summary__total"><dt>Total</dt><dd>{formatCurrency(orderTotal)}</dd></div></dl>
        </aside>
      </form>
    </div>
  );
};

export default CheckoutPage;
